import React, { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import PdaDiagnostics from './PdaDiagnostics'
import { keepPortrait, uprightRotation } from '../../utils/orientation'
import '../../styles/pda.css'

/**
 * Full-screen chrome for the handheld screens.
 *
 * Deliberately outside the main app Layout: the sidebar, drawer and mobile
 * header together eat most of a 4" panel, and none of them are reachable with
 * a thumb while the operator is holding stock. The three functions get the
 * whole screen and a permanent tab strip.
 *
 * One device, several people: the header doubles as a sign-out so the next
 * operator's moves are attributed to them rather than to whoever logged in
 * that morning.
 *
 * The tabs navigate with `replace`. Pushing would stack a history entry per
 * tab, so the hardware back button walked backwards through previously viewed
 * tabs — tap Look Up, then Putaway, then press back and you are on Look Up
 * again for no reason the operator can see. Back now leaves the handheld
 * rather than shuffling it.
 */
const TABS = [
    { to: '/pda/putaway', label: 'Putaway' },
    { to: '/pda/lookup', label: 'Look Up' },
    { to: '/pda/consolidate', label: 'Consolidate' }
]

export default function PdaShell() {
    const { user, logout } = useAuth()
    useHandheldManifest()
    usePortraitLock()
    const rotation = useUprightRotation()
    const { diagnosticsOpen, closeDiagnostics, onTitleTap } = useDiagnosticsGesture()

    return (
        <div className="pda" data-rotate={rotation || undefined}>
            <div className="pda-header">
                <strong onClick={onTitleTap} style={{ cursor: 'default' }}>Prism Handheld</strong>
                <button className="pda-who" onClick={logout} title="Sign out">
                    {user?.name || user?.email || 'Signed in'} · Switch
                </button>
            </div>

            <nav className="pda-tabs">
                {TABS.map(({ to, label }) => (
                    <NavLink
                        key={to}
                        to={to}
                        replace
                        className={({ isActive }) => `pda-tab${isActive ? ' is-active' : ''}`}
                    >
                        {label}
                    </NavLink>
                ))}
            </nav>

            <div className="pda-body">
                {diagnosticsOpen ? <PdaDiagnostics onClose={closeDiagnostics} /> : <Outlet />}
            </div>
        </div>
    )
}

/**
 * Advertise the web app manifest only while a handheld screen is mounted.
 *
 * A manifest linked from index.html would offer "Install Prism" to everyone on
 * every device, and start them on the handheld screens. Attaching it here keeps
 * the install prompt where it is wanted — on the PDA, where running full-screen
 * buys back the address bar on a 4" panel — and changes nothing for anyone
 * using Prism in a normal browser.
 */
function useHandheldManifest() {
    useEffect(() => {
        const manifest = document.createElement('link')
        manifest.rel = 'manifest'
        manifest.href = '/manifest.webmanifest'

        const themeColour = document.createElement('meta')
        themeColour.name = 'theme-color'
        themeColour.content = '#0f172a'

        document.head.append(manifest, themeColour)
        return () => { manifest.remove(); themeColour.remove() }
    }, [])
}

/**
 * Hold the handheld in portrait for as long as the screens are open.
 *
 * The mechanics live in utils/orientation: locking has to wait for fullscreen
 * rather than race it, the first attempt can legitimately fail before anyone
 * has touched the screen, and a lock can be dropped when the app is
 * backgrounded. All this does is scope it to the handheld screens and say when
 * fullscreen is appropriate.
 *
 * Fullscreen is gated on a small touch device so that opening these screens on
 * a desktop to look at them does not throw the window into fullscreen
 * unannounced.
 */
const HANDHELD_MAX_WIDTH = 480

function usePortraitLock() {
    useEffect(() => keepPortrait(isHandheld), [])
}

function isHandheld() {
    return navigator.maxTouchPoints > 0 && window.innerWidth <= HANDHELD_MAX_WIDTH
}

/**
 * Turn the page back when the device insists on being upside down.
 *
 * The lock is still the first line and usually the only one needed. This
 * exists because the device reported "lock: granted" while sitting at 180
 * degrees, and no amount of asking more politely will move a window the
 * browser has already agreed to leave alone. Rotating our own page needs no
 * permission and cannot be refused.
 */
function useUprightRotation() {
    const [rotation, setRotation] = useState(() => uprightRotation(screen?.orientation?.angle))

    useEffect(() => {
        const update = () => setRotation(uprightRotation(screen?.orientation?.angle))
        update()
        screen?.orientation?.addEventListener?.('change', update)
        window.addEventListener('resize', update)
        return () => {
            screen?.orientation?.removeEventListener?.('change', update)
            window.removeEventListener('resize', update)
        }
    }, [])

    return rotation
}

/**
 * Three taps on the title opens the diagnostics panel.
 *
 * A handheld launched from its home-screen icon has no address bar, so there
 * is no way to reach a debug URL. This gives one that costs the operator
 * nothing: three deliberate taps inside two seconds will not happen by
 * accident, and there is no visible control to be tapped by mistake.
 */
const TAPS_TO_OPEN = 3
const TAP_WINDOW_MS = 2000

function useDiagnosticsGesture() {
    const [diagnosticsOpen, setDiagnosticsOpen] = useState(false)
    const taps = useRef([])

    function onTitleTap() {
        const now = Date.now()
        taps.current = [...taps.current, now].filter((at) => now - at < TAP_WINDOW_MS)
        if (taps.current.length >= TAPS_TO_OPEN) {
            taps.current = []
            setDiagnosticsOpen(true)
        }
    }

    return {
        diagnosticsOpen,
        closeDiagnostics: () => setDiagnosticsOpen(false),
        onTitleTap
    }
}
